/**
 * Server checkpoint slot map — the single place that says "workflow step X is
 * stored under server `step` key Y, in format Z".
 *
 * Lives in its own module (rather than next to the upload code) because both the
 * uploader (`lib/stepCheckpointUpload.js`) and the meta reader
 * (`lib/bkScanSourceFiles.js`) need it, and importing one from the other would
 * be circular.
 *
 * Historically only `rectification`, `locking` and `scan_final_preview` existed
 * server-side, so several steps were squeezed into a neighbour's slot. Those
 * work-arounds are recorded as `LEGACY_SLOT_FALLBACK` and stay in force until
 * the backend allowlist covers the full step list — see `stepCheckpointUpload`.
 */
import {
  GLOBAL_STEP_ORDER,
  MAIN_PROCESS_STEPS,
  SCAN_PROCESS_STEPS,
} from './flowConfig';
import { EXPORT_FORMATS } from '../lib/exportConfig';

/** Bump when the metadata envelope shape changes. */
export const STEP_CHECKPOINT_METADATA_VERSION = 1;

/**
 * Server `step` slot key per workflow step. FINALIZATION is absent on purpose —
 * it is stored through the dedicated final-STL endpoint, not the checkpoint slot.
 */
export const CHECKPOINT_SLOT_BY_STEP = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: 'preparing_scan',
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: 'axes_alignment',
  [MAIN_PROCESS_STEPS.RECTIFICATION]: 'rectification',
  [MAIN_PROCESS_STEPS.ELONGATION]: 'elongation',
  [MAIN_PROCESS_STEPS.TRIMLINE]: 'trimline',
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: 'ml_reduction',
  [MAIN_PROCESS_STEPS.FLARES]: 'flares',
  [MAIN_PROCESS_STEPS.THICKNESS]: 'thickness',
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: 'locking',
  [MAIN_PROCESS_STEPS.VALVE]: 'valve',
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: 'logo_placement',
};

/** Reverse lookup for reading `get_bk_design_files_meta` → `checkpoints.<slot>`. */
export const STEP_BY_CHECKPOINT_SLOT = Object.fromEntries(
  Object.entries(CHECKPOINT_SLOT_BY_STEP).map(([step, slot]) => [slot, step]),
);

/**
 * Export format per step. Clinical steps keep vertex colours (PLY) because the
 * colour map is clinically meaningful; once the locking attachment is cut the
 * mesh is manufacturing geometry, so STL.
 */
export const CHECKPOINT_FORMAT_BY_STEP = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: EXPORT_FORMATS.PLY,
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.RECTIFICATION]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.ELONGATION]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.TRIMLINE]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.ML_REDUCTION]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.FLARES]: EXPORT_FORMATS.PLY,
  [MAIN_PROCESS_STEPS.THICKNESS]: EXPORT_FORMATS.PLY,
  // Valve now runs after locking, so its socket already carries the attachment
  // cut — manufacturing geometry with no vertex colours left to preserve.
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: EXPORT_FORMATS.STL,
  [MAIN_PROCESS_STEPS.VALVE]: EXPORT_FORMATS.STL,
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: EXPORT_FORMATS.STL,
};

/**
 * Pre-per-step-slot storage locations, kept so a frontend deploy that lands
 * before the backend widening still reads and writes what exists today.
 *
 * `slot` — server step key to use instead.
 * `variant` — locking variant to write into / read from.
 * `tagStep` — value written to `metadata.checkpoint_step` so a shared slot can
 *   still be attributed to the step that produced it.
 */
export const LEGACY_SLOT_FALLBACK = {
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: { slot: 'scan_final_preview' },
  // Thickness is the last clinical step now that valve moved after locking, so it
  // is the one that carries the fully-modified clinical socket into the shared
  // `rectification` slot (the pre-locking baseline everything else restores from).
  [MAIN_PROCESS_STEPS.THICKNESS]: { slot: 'rectification', tagStep: 'thickness' },
  [MAIN_PROCESS_STEPS.VALVE]: { slot: 'locking', variant: 'plain', tagStep: 'valve' },
  [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: {
    slot: 'locking',
    variant: 'plain',
    tagStep: 'logo_placement',
  },
};

/** Steps that carry a mesh checkpoint, in workflow order. */
export const CHECKPOINT_STEPS = GLOBAL_STEP_ORDER.filter(
  (step) => CHECKPOINT_SLOT_BY_STEP[step] != null,
);

export function checkpointSlotForStep(stepId) {
  return CHECKPOINT_SLOT_BY_STEP[stepId] ?? null;
}

export function checkpointFormatForStep(stepId) {
  return CHECKPOINT_FORMAT_BY_STEP[stepId] ?? EXPORT_FORMATS.PLY;
}
